import React, { useEffect, useState, useRef } from "react";
import { useSelector } from "react-redux";
import { DataMainTemplate } from "@nocios/presentation-kit";
import Config from "src/Constants/Config";
import setCurrentPage from "src/Utils/setCurrentPage";

// Constants
const PAGE_ID = 2;
const NEXT_PAGE_ID = PAGE_ID + 1;
const BACK_PAGE_ID = PAGE_ID - 1;
const LAST_PAGE = false;
const BG_ID = 4;
const BG_TYPE = "jpg";

const LEFT_TITLE = "Infused Intelligence to overcome\nNetwork Design challenges";

interface SliderWrapperProps {
  children: React.ReactNode;
}

const SliderWrapper = ({ children }: SliderWrapperProps) => (
  <div
    style={{
      position: "relative",
      overflow: "hidden",
      width: "100%", // Ensures the wrapper fills the screen
    }}
  >
    {children}
  </div>
);

// Image slider component remains unchanged
const ImageSlider = () => {
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  useEffect(() => {
    setIsVisible(false); // Reset visibility when page changes
    const timer = setTimeout(() => {
      setIsVisible(true);
    }, 300);

    return () => clearTimeout(timer);
  }, [currentPage]);

  const images = [1, 2, 3];
  const GAP = "calc(-1 * var(--width-100))";

  return (
    <div
      ref={containerRef}
      style={{
        position: "absolute",
        bottom: "-6%", // Position above buttons
        left: "0", // Start from left side
        width: "100%", // Cover most of the width
        height: "auto",
        pointerEvents: "none",
        zIndex: 10,
        display: "flex",
        justifyContent: "center",
        overflow: "hidden", // Prevent overflow scrollbars
      }}
    >
      {images.map((index, i) => (
        <div
          key={index}
          style={{
            marginRight: i < images.length - 1 ? GAP : 0,
            opacity: isVisible ? 1 : 0,
            transform: `translateX(${isVisible ? 0 : "-100%"})`,
            transition: `opacity 0.7s ease-out, transform 0.7s ease-out`,
            transitionDelay: `${i * 0.3}s`,
          }}
        >
          <img
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/right_${index}.png`}
            alt={`Presentation Data ${index}`}
            style={{
              width: "var(--width-420)",
              height: "auto",
              display: "block",
            }}
          />
        </div>
      ))}
    </div>
  );
};

// Main data function that returns the template data
const DATA = (): DataMainTemplate => {
  const currentPage = useSelector((state: { appReducer: { currentPage: number } }) => state.appReducer.currentPage);

  const baseImageBG = `${Config.BASE_URL}/images/BG/`;

  const leftAfterDescription = (
    <div style={{ padding: "var(--width-10) 0" }}>
      <img
        src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/left_separator.png`}
        alt="separator"
        style={{
          width: "var(--width-605)",
          height: "auto",
        }}
      />
      <div
        style={{
          color: "#fff",
          fontFamily: "Myriad Pro",
          fontSize: "var(--width-24)",
          fontStyle: "normal",
          fontWeight: "380",
          lineHeight: "150%",
          whiteSpace: "pre-line",
        }}
      >
        <div
          style={{
            marginTop: "var(--width-30)",
          }}
        >
          TOSCA service templates are often curated manually by
          <br />
          network designers, thus involving multiple challenges.
        </div>
        <ul
          style={{
            marginBottom: "var(--width-10)",
          }}
        >
          <li style={{ marginTop: "var(--height-15)" }}>
            Heavy dependency on network designer subject
            <br />
            matter Experts (SMEs)
          </li>
          <li style={{ marginTop: "var(--height-15)" }}>Lack of uniform standard templates</li>
          <li style={{ marginTop: "var(--height-15)" }}>
            Time-consuming manual effort required to retrieve
            <br />
            and customize similar templates
          </li>
        </ul>
      </div>
    </div>
  );

  const backButton =
    BACK_PAGE_ID > 0
      ? {
          label: "Back",
          type: "back" as const,
          startIcon: { icon: "arrow-right", color: "#fff" },
          onClick: () => setCurrentPage(BACK_PAGE_ID),
        }
      : undefined;

  const nextButton = !LAST_PAGE
    ? {
        label: "Next",
        type: "next" as const,
        endIcon: { icon: "arrow-left" },
        onClick: () => setCurrentPage(NEXT_PAGE_ID),
      }
    : undefined;

  return {
    styleBody: { minWidth: "var(--width-624)", marginTop: "var(--height-50)" },
    bgVideo: {
      poster: `${baseImageBG}BG_${BG_ID}.${BG_TYPE}`,
      video: `${Config.BASE_URL}/videos/bg_${PAGE_ID}.mp4`,
    },
    gradient:
      "linear-gradient(270deg, rgba(21, 50, 121, 0.00) 11.77%, #153279 87.81%), linear-gradient(0deg, rgba(0, 0, 0, 0.60) 0%, rgba(0, 0, 0, 0.60) 100%)",
    title: LEFT_TITLE,
    description:
      "TOSCA, Topology and Orchestration Specification for\ncloud applications, is YAML template based de facto\nstandard for defining blueprint of network topology,\nfunctions, connectivity and services.",
    leftAfterDescription: leftAfterDescription,
    // Combine the VideoPlayer and ImageSlider in the right side component
    rightSideComponent: (
      <SliderWrapper>
        <div style={{ paddingLeft: "var(--width-80)", paddingTop: "var(--height-20)" }}>
          <img alt="" style={{ width: "var(--width-174)" }} src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/tosca.png`} />
          <div
            style={{
              color: "#FFF",
              fontFamily: "Myriad Pro",
              fontSize: "var(--width-26)",
              fontStyle: "normal",
              fontWeight: "380",
              lineHeight: "140%",
            }}
          >
            Leveraged Anthropic Claude Sonnet 3.5 LLM from AWS Bedrock
            <br />
            to transform User Prompts to Service Templates
          </div>
          <img
            alt=""
            style={{ width: "var(--width-935)", height: "var(--height-440)", marginTop: "var(--height-24)" }}
            src={`${Config.BASE_URL}/images/screen_${PAGE_ID}/diagram.png`}
          />
          <div></div>
        </div>
        <ImageSlider key={currentPage} />
      </SliderWrapper>
    ),
    next: nextButton,
    back: backButton,
  };
};

export default DATA;
